import axios from 'axios';
import { API_URL } from '../config/api';
import { getRealmBridge, realmAvailable, setRealmOpenCached } from './realmBridge';
import { isRealmEnabled } from './isRealmEnabled';
import { emitDesktopBoot } from '../utils/desktopBoot';

const DELTA_INTERVAL_MS = 30_000;

let startedForUserId = null;
let deltaTimer = null;
let wsWired = false;
let messageListener = null;

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const normalizeForUi = (doc) => {
  if (!doc || typeof doc !== 'object') return doc;
  const out = { ...doc };
  const id = out.id || out._id;
  if (id != null) {
    out.id = id;
    out._id = id;
  }
  return out;
};

const displayNameFromUser = (user) => {
  if (!user) return null;
  const first = (user.firstName || '').trim();
  const last = (user.lastName || '').trim();
  if (first && last) return `${first} ${last}`;
  if (first) return first;
  if (last) return last;
  const username = (user.username || '').trim();
  return username || null;
};

/** Fill senderName / senderProfilePicture from synced UserProfile when missing. */
const enrichMessagesWithSenders = async (messages) => {
  const list = Array.isArray(messages) ? messages : [];
  if (!list.length) return list;

  const needIds = [...new Set(
    list
      .filter((msg) => {
        if (!msg?.senderId) return false;
        const hasName = Boolean(msg.senderName && String(msg.senderName).trim());
        return !hasName || !msg.senderProfilePicture;
      })
      .map((msg) => String(msg.senderId))
      .filter(Boolean),
  )];

  if (!needIds.length) {
    return list.map(normalizeForUi);
  }

  const usersById = new Map();
  await Promise.all(needIds.map(async (id) => {
    const user = await getRealmDoc('UserProfile', id);
    if (user) usersById.set(String(id), user);
  }));

  return list.map((msg) => {
    const out = normalizeForUi(msg);
    if (!out?.senderId) return out;
    const user = usersById.get(String(out.senderId));
    if (!user) return out;
    const name = displayNameFromUser(user);
    if (name && !(out.senderName && String(out.senderName).trim())) {
      out.senderName = name;
    }
    if (!out.senderProfilePicture && user.profilePicture) {
      out.senderProfilePicture = user.profilePicture;
    }
    return out;
  });
};

/** Push message docs into Redux (deduped by addMessage). */
export const pushMessagesToRedux = async (messages, { skipUnreadCountIncrement = true } = {}) => {
  const list = Array.isArray(messages) ? messages : [];
  if (!list.length) return;
  const { store } = await import('../store');
  const { addMessage } = await import('../store/slices/chatSlice');
  const currentUserId = store.getState().auth?.user?.id;
  const enriched = await enrichMessagesWithSenders(list.map(normalizeForUi));
  enriched.forEach((msg) => {
    const channelId = msg?.channelId;
    const id = msg?.id || msg?._id;
    if (!channelId || !id) return;
    store.dispatch(addMessage({
      channelId,
      message: { ...msg, id, channelId },
      currentUserId,
      skipUnreadCountIncrement,
    }));
  });
};

/** Read recent cached messages per channel and merge into Redux. */
export const syncRecentChannelMessagesToRedux = async (
  channelIds = [],
  { limit = 15 } = {},
) => {
  const ids = [...new Set((channelIds || []).filter(Boolean).map(String))];
  if (!ids.length) return;
  const batch = [];
  for (const channelId of ids) {
    const msgs = await readMessagesFromRealm(channelId, { limit });
    if (msgs?.length) batch.push(...msgs);
  }
  await pushMessagesToRedux(batch);
};

export const startRealmSync = async (userId) => {
  if (!isRealmEnabled() || !userId) {
    emitDesktopBoot({ percent: 100, done: true, label: 'Ready', detail: 'Opening Workloop' });
    return { skipped: true };
  }
  emitDesktopBoot({ percent: 18, label: 'Syncing...', detail: 'Opening local workspace' });
  const bridge = getRealmBridge();
  await bridge.open(userId);
  setRealmOpenCached(true);
  startedForUserId = userId;

  try {
    emitDesktopBoot({ percent: 36, label: 'Syncing...', detail: 'Downloading latest chats' });
    const response = await axios.get(`${API_URL}/sync/bootstrap`, {
      headers: authHeaders(),
      params: { messagesPerChannel: 80 },
    });
    emitDesktopBoot({ percent: 55, label: 'Syncing...', detail: 'Saving workspace' });
    await bridge.applyBootstrap(response.data);
    if (response.data?.serverCursor) {
      await bridge.setMeta('lastServerCursor', response.data.serverCursor);
    }
    if (Array.isArray(response.data?.liveTrackingItems)) {
      const { replaceLiveTrackingFromBootstrap } = await import('./liveTrackingRealm');
      await replaceLiveTrackingFromBootstrap(response.data.liveTrackingItems);
    }
    if (response.data?.messages?.length) {
      await pushMessagesToRedux(response.data.messages);
    }
  } catch (err) {
    console.warn('[realm] bootstrap failed', err?.message || err);
  }

  emitDesktopBoot({ percent: 72, label: 'Loading...', detail: 'Preparing channels and tasks' });
  await hydrateUiFromRealm();
  scheduleDeltaPull();
  try {
    emitDesktopBoot({ percent: 88, label: 'Loading...', detail: 'Finishing sync' });
    const { store } = await import('../store');
    const { flushOutboundMessages } = await import('../store/slices/chatSlice');
    const { flushOutboundTasks } = await import('../store/slices/taskSlice');
    const { flushOutboundReminderQueue } = await import('./outboundQueue');
    const { flushLiveTrackingOutbound } = await import('./liveTrackingRealm');
    await store.dispatch(flushOutboundMessages());
    await store.dispatch(flushOutboundTasks());
    await flushOutboundReminderQueue();
    await flushLiveTrackingOutbound();
    const { loadLiveTracking, recomputeLiveTrackingAttention } = await import('../store/slices/liveTrackingSlice');
    await store.dispatch(loadLiveTracking());
    store.dispatch(recomputeLiveTrackingAttention());
  } catch {
    // ignore
  }
  emitDesktopBoot({ percent: 100, done: true, label: 'Ready', detail: 'Opening Workloop' });
  return { ok: true, userId };
};

export const stopRealmSync = async ({ wipe = false } = {}) => {
  if (deltaTimer) {
    clearInterval(deltaTimer);
    deltaTimer = null;
  }
  startedForUserId = null;
  setRealmOpenCached(false);
  const bridge = getRealmBridge();
  try {
    await bridge.close({ wipe });
  } catch (err) {
    console.warn('[realm] close failed', err?.message || err);
  }
};

const scheduleDeltaPull = () => {
  if (deltaTimer) clearInterval(deltaTimer);
  deltaTimer = setInterval(() => {
    pullDelta().catch(() => {});
  }, DELTA_INTERVAL_MS);
};

export const pullDelta = async () => {
  if (!isRealmEnabled() || !startedForUserId) return null;
  const bridge = getRealmBridge();
  if (!(await bridge.isOpen())) return null;
  const since = (await bridge.getMeta('lastServerCursor'))
    || (await bridge.getMeta('lastSyncedAt'))
    || null;
  if (!since) return null;
  try {
    const response = await axios.get(`${API_URL}/sync/delta`, {
      headers: authHeaders(),
      params: { since, messagesPerChannel: 50 },
    });
    await bridge.applyBootstrap(response.data);
    if (response.data?.serverCursor) {
      await bridge.setMeta('lastServerCursor', response.data.serverCursor);
    }
    if (response.data?.messages?.length) {
      await pushMessagesToRedux(response.data.messages);
    }
    try {
      const { store } = await import('../store');
      const { flushOutboundMessages } = await import('../store/slices/chatSlice');
      const { flushOutboundTasks, fetchTasks } = await import('../store/slices/taskSlice');
      const { fetchChannels, fetchDirectMessages } = await import('../store/slices/channelSlice');
      const { flushOutboundReminderQueue } = await import('./outboundQueue');
      const { fetchOrgWorkflowReactions } = await import('../store/slices/workflowCatalogSlice');
      await store.dispatch(flushOutboundMessages());
      await store.dispatch(flushOutboundTasks());
      await flushOutboundReminderQueue();
      const { flushLiveTrackingOutbound } = await import('./liveTrackingRealm');
      await flushLiveTrackingOutbound();
      store.dispatch(fetchOrgWorkflowReactions());
      if (response.data?.tasks?.length) {
        store.dispatch(fetchTasks({}));
      }
      if (response.data?.liveTrackingItems?.length) {
        const { mergeLiveTrackingFromSync } = await import('../store/slices/liveTrackingSlice');
        await store.dispatch(mergeLiveTrackingFromSync(response.data.liveTrackingItems));
      }
      const { recomputeLiveTrackingAttention } = await import('../store/slices/liveTrackingSlice');
      store.dispatch(recomputeLiveTrackingAttention());
      if (response.data?.channels?.length) {
        store.dispatch(fetchChannels());
        store.dispatch(fetchDirectMessages());
      }
    } catch {
      // ignore
    }
    return response.data;
  } catch (err) {
    console.warn('[realm] delta failed', err?.message || err);
    return null;
  }
};

export const upsertRealmDocs = async (type, docs) => {
  if (!docs) return;
  const list = Array.isArray(docs) ? docs : [docs];
  if (!list.length) return;
  if (!(await realmAvailable())) return;
  const bridge = getRealmBridge();
  await bridge.upsert(type, list.map(normalizeForUi));
};

export const deleteRealmDoc = async (type, id) => {
  if (!id || !(await realmAvailable())) return;
  const bridge = getRealmBridge();
  await bridge.delete(type, id);
};

export const queryRealm = async (opts) => {
  if (!(await realmAvailable())) return [];
  const bridge = getRealmBridge();
  const rows = await bridge.query(opts);
  return (rows || []).map(normalizeForUi);
};

export const getRealmDoc = async (type, id) => {
  if (!id || !(await realmAvailable())) return null;
  const bridge = getRealmBridge();
  return normalizeForUi(await bridge.get(type, id));
};

/** Prefer Realm channels when local DB is open; otherwise null. */
export const readChannelsFromRealm = async () => {
  if (!(await realmAvailable())) return null;
  const channels = await queryRealm({ type: 'Channel' });
  if (!channels.length) return null;
  return channels.map(normalizeForUi);
};

export const readChannelFromRealm = async (channelId) => {
  if (!channelId) return null;
  return getRealmDoc('Channel', channelId);
};

export const readUsersFromRealm = async () => {
  if (!(await realmAvailable())) return null;
  const users = await queryRealm({ type: 'UserProfile' });
  if (!users.length) return null;
  return users.map(normalizeForUi);
};

export const readUserFromRealm = async (userId) => {
  if (!userId) return null;
  return getRealmDoc('UserProfile', userId);
};

export const readUsersByIdsFromRealm = async (ids = []) => {
  const wanted = [...new Set((ids || []).filter(Boolean).map(String))];
  if (!wanted.length || !(await realmAvailable())) return [];
  const found = [];
  for (const id of wanted) {
    const user = await getRealmDoc('UserProfile', id);
    if (user) found.push(user);
  }
  return found;
};

export const readLastViewedFromRealm = async () => {
  if (!(await realmAvailable())) return null;
  const views = await queryRealm({ type: 'UserChannelView' });
  if (!views.length) return null;
  const map = {};
  views.forEach((view) => {
    const channelId = view?.channelId;
    if (!channelId || !view.lastViewed) return;
    map[channelId] = view.lastViewed;
  });
  return Object.keys(map).length ? map : null;
};

export const readLatestActivityByChannelFromRealm = async (channelIds) => {
  if (!(await realmAvailable())) return null;
  const wanted = Array.isArray(channelIds) && channelIds.length
    ? new Set(channelIds.map(String))
    : null;
  const messages = await queryRealm({ type: 'Message' });
  if (!messages.length) return wanted ? Object.fromEntries([...wanted].map((id) => [id, 0])) : {};
  const latest = {};
  messages.forEach((msg) => {
    const channelId = String(msg?.channelId || '');
    if (!channelId || (wanted && !wanted.has(channelId))) return;
    const ts = new Date(msg.createdAt || msg.timestamp || 0).getTime();
    if (!Number.isFinite(ts) || ts <= 0) return;
    if (!latest[channelId] || ts > latest[channelId]) {
      latest[channelId] = ts;
    }
  });
  if (wanted) {
    wanted.forEach((id) => {
      if (latest[id] == null) latest[id] = 0;
    });
  }
  return latest;
};

export const computeUnreadCountsFromRealm = async (channelIds, lastViewedMap, currentUserId) => {
  const ids = (channelIds || []).filter(Boolean).map(String);
  if (!ids.length || !(await realmAvailable())) return null;
  const messages = await queryRealm({ type: 'Message' });
  const counts = Object.fromEntries(ids.map((id) => [id, 0]));
  const idSet = new Set(ids);
    const lastViewed = lastViewedMap || {};
    messages.forEach((msg) => {
      const channelId = String(msg?.channelId || '');
      if (!idSet.has(channelId)) return;
      if (currentUserId && String(msg.senderId) === String(currentUserId)) return;
      const ts = new Date(msg.createdAt || msg.timestamp || 0).getTime();
      if (!Number.isFinite(ts) || ts <= 0) return;
      const viewedRaw = lastViewed[channelId] || lastViewed[msg?.channelId];
      const viewedTs = viewedRaw ? new Date(viewedRaw).getTime() : 0;
      if (viewedTs && ts <= viewedTs) return;
      counts[channelId] += 1;
    });
  return counts;
};

const hydrateUiFromRealm = async () => {
  try {
    const { store } = await import('../store');
    const { fetchUsers } = await import('../store/slices/userSlice');
    const { fetchChannels, fetchDirectMessages } = await import('../store/slices/channelSlice');
    const { fetchLastViewed, fetchUnreadCounts } = await import('../store/slices/chatSlice');
    const { fetchTasks } = await import('../store/slices/taskSlice');
    await store.dispatch(fetchUsers());
    const { fetchOrgWorkflowReactions } = await import('../store/slices/workflowCatalogSlice');
    await Promise.all([
      store.dispatch(fetchChannels()),
      store.dispatch(fetchDirectMessages()),
      store.dispatch(fetchLastViewed()),
      store.dispatch(fetchTasks({})),
      store.dispatch(fetchOrgWorkflowReactions()),
    ]);
    try {
      const { loadLiveTracking, recomputeLiveTrackingAttention } = await import('../store/slices/liveTrackingSlice');
      await store.dispatch(loadLiveTracking());
      await store.dispatch(recomputeLiveTrackingAttention());
    } catch {
      // ignore
    }
    const state = store.getState();
    const channelIds = [
      ...(state.channels?.channels || []),
      ...(state.channels?.directMessages || []),
    ].map((channel) => channel?.id).filter(Boolean);
    if (channelIds.length) {
      store.dispatch(fetchUnreadCounts({
        channelIds,
        lastViewed: state.chat?.lastViewed,
      }));
    }
  } catch (err) {
    console.warn('[realm] ui hydrate failed', err?.message || err);
  }
};

/** All locally cached messages for a channel (no 80-message cap). */
export const readChannelMessagesFromRealm = async (channelId) => {
  if (!channelId || !(await realmAvailable())) return null;
  const messages = await queryRealm({
    type: 'Message',
    filter: 'channelId == $0',
    args: [String(channelId)],
    sort: [['createdAt', true]],
  });
  return messages.length ? enrichMessagesWithSenders(messages) : null;
};

const messageTime = (msg) => {
  const ts = new Date(msg?.createdAt || msg?.timestamp || 0).getTime();
  return Number.isFinite(ts) ? ts : 0;
};

const messageIdOf = (msg) => String(msg?.id || msg?._id || '');

/** Older messages than `beforeDate`, newest-of-older first then returned oldest→newest. */
export const readOlderMessagesFromRealm = async (
  channelId,
  { beforeDate, size = 30, excludeIds = [] } = {},
) => {
  const all = await readChannelMessagesFromRealm(channelId);
  if (!all?.length) return [];
  const exclude = new Set((excludeIds || []).map((id) => String(id)));
  const beforeTs = beforeDate ? new Date(beforeDate).getTime() : NaN;
  const older = all.filter((msg) => {
    const id = messageIdOf(msg);
    if (!id || exclude.has(id)) return false;
    if (!Number.isFinite(beforeTs)) return true;
    return messageTime(msg) < beforeTs;
  });
  older.sort((a, b) => messageTime(b) - messageTime(a));
  return older.slice(0, size).sort((a, b) => messageTime(a) - messageTime(b));
};

/** Window of local messages around a target id (for jump-to-message / workflows). */
export const readMessagesAroundTargetFromRealm = async (
  channelId,
  messageId,
  { before = 40, after = 15 } = {},
) => {
  if (!messageId) return null;
  let scopedChannelId = channelId;
  let all = scopedChannelId ? await readChannelMessagesFromRealm(scopedChannelId) : null;
  let sorted = (all || []).slice().sort((a, b) => messageTime(a) - messageTime(b));
  let idx = sorted.findIndex((msg) => messageIdOf(msg) === String(messageId));
  if (idx < 0) {
    const doc = await getRealmDoc('Message', messageId);
    if (!doc) return null;
    scopedChannelId = doc.channelId || scopedChannelId;
    all = scopedChannelId ? await readChannelMessagesFromRealm(scopedChannelId) : [doc];
    sorted = (all || [doc]).slice().sort((a, b) => messageTime(a) - messageTime(b));
    idx = sorted.findIndex((msg) => messageIdOf(msg) === String(messageId));
    if (idx < 0) {
      sorted.push(doc);
      sorted.sort((a, b) => messageTime(a) - messageTime(b));
      idx = sorted.findIndex((msg) => messageIdOf(msg) === String(messageId));
    }
  }
  if (idx < 0) return null;
  const start = Math.max(0, idx - before);
  const end = Math.min(sorted.length, idx + 1 + after);
  return {
    channelId: scopedChannelId,
    messages: sorted.slice(start, end),
    hasMoreBefore: start > 0,
    hasMoreAfter: end < sorted.length,
  };
};

/** Prefer Realm messages for a channel when available. */
export const readMessagesFromRealm = async (channelId, { limit = 80 } = {}) => {
  if (!channelId || !(await realmAvailable())) return null;
  const messages = await queryRealm({
    type: 'Message',
    filter: 'channelId == $0',
    args: [channelId],
    sort: [['createdAt', true]],
    limit,
  });
  if (!messages.length) return null;
  // query returns newest-first when sorted desc; chat UI expects chronological / API page shape
  const enriched = await enrichMessagesWithSenders(messages);
  return enriched.sort((a, b) => {
      const at = a.createdAt || '';
      const bt = b.createdAt || '';
      return at < bt ? 1 : at > bt ? -1 : 0;
    });
};

export const readTasksFromRealm = async () => {
  if (!(await realmAvailable())) return null;
  const tasks = await queryRealm({ type: 'Task' });
  if (!tasks.length) return null;
  return tasks.map((task) => {
    const status = task.status || task.statusString || 'PENDING';
    return {
      ...task,
      id: task.id || task._id,
      status: typeof status === 'string' ? status : String(status),
    };
  });
};

export const saveAssignedWorkflowsToRealm = async (items) => {
  if (!(await realmAvailable())) return;
  const list = Array.isArray(items) ? items : [];
  const bridge = getRealmBridge();
  await bridge.setMeta('assignedWorkflows', JSON.stringify(list));
};

export const readAssignedWorkflowsFromRealm = async () => {
  if (!(await realmAvailable())) return null;
  const bridge = getRealmBridge();
  const raw = await bridge.getMeta('assignedWorkflows');
  if (!raw) return null;
  try {
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : null;
  } catch {
    return null;
  }
};

export const readRemindersFromRealm = async () => {
  if (!(await realmAvailable())) return null;
  const reminders = await queryRealm({ type: 'Reminder' });
  return reminders.length ? reminders : null;
};

const appointmentDateKey = (value) => String(value || '').slice(0, 10);

export const readAppointmentsFromRealm = async ({ channelId, from, to } = {}) => {
  if (!(await realmAvailable())) return null;
  const rows = await queryRealm({ type: 'Appointment' });
  if (!rows.length) return null;
  const fromKey = appointmentDateKey(from);
  const toKey = appointmentDateKey(to);
  const wantedChannel = channelId ? String(channelId) : null;
  const filtered = rows.filter((appt) => {
    if (wantedChannel && String(appt.channelId || '') !== wantedChannel) return false;
    const dateKey = appointmentDateKey(appt.date);
    if (!dateKey) return false;
    if (fromKey && dateKey < fromKey) return false;
    if (toKey && dateKey > toKey) return false;
    return true;
  });
  return filtered.map(normalizeForUi);
};

export const readSavedMessagesFromRealm = async () => {
  if (!(await realmAvailable())) return null;
  const saved = await queryRealm({
    type: 'SavedMessage',
    sort: [['createdAt', true]],
  });
  if (!saved.length) return null;
  const hydrated = [];
  for (const row of saved) {
    const sm = normalizeForUi(row);
    let message = sm.message;
    if (!message?.id && sm.messageId) {
      message = await getRealmDoc('Message', sm.messageId);
    }
    hydrated.push({
      ...sm,
      id: sm.id || sm._id,
      message: message || null,
      channelId: sm.channelId || message?.channelId || null,
    });
  }
  return hydrated;
};

export const readAttendanceEventsFromRealm = async () => {
  if (!(await realmAvailable())) return null;
  const events = await queryRealm({ type: 'AttendanceEvent' });
  return events.length ? events : null;
};

export const readProductivityEntriesFromRealm = async () => {
  if (!(await realmAvailable())) return null;
  const entries = await queryRealm({ type: 'ProductivityEntry' });
  return entries.length ? entries.map(normalizeForUi) : null;
};

export const readTemplatesFromRealm = async () => {
  if (!(await realmAvailable())) return null;
  const templates = await queryRealm({ type: 'MessageTemplate' });
  return templates.length ? templates : null;
};

export const readOrgWorkflowReactionsFromRealm = async () => {
  if (!(await realmAvailable())) return null;
  const rows = await queryRealm({ type: 'OrgWorkflowReaction' });
  return rows.length ? rows : null;
};

/**
 * Wire WebSocket payloads into Realm (call once with the shared websocketService).
 */
export const wireRealmWebSocketWriters = (websocketService) => {
  if (!isRealmEnabled() || wsWired || !websocketService) return;
  wsWired = true;

  const originalSubscribe = websocketService.subscribeToChannel?.bind(websocketService);
  if (originalSubscribe) {
    websocketService.subscribeToChannel = (channelId, callback) => {
      const wrapped = (data) => {
        if (typeof callback === 'function') callback(data);
        try {
          if (data && (data.id || data._id) && data.channelId) {
            upsertRealmDocs('Message', data);
          } else if (data?.type === 'MESSAGE_DELETED' && data.messageId) {
            deleteRealmDoc('Message', data.messageId);
          } else if (data?.deletedMessageId) {
            deleteRealmDoc('Message', data.deletedMessageId);
          }
        } catch (err) {
          console.warn('[realm] ws message write failed', err);
        }
      };
      return originalSubscribe(channelId, wrapped);
    };
  }

  const wrapUserQueue = (subscribeFnName, realmType) => {
    const original = websocketService[subscribeFnName]?.bind(websocketService);
    if (!original) return;
    websocketService[subscribeFnName] = (callback) => {
      const wrapped = (data) => {
        if (typeof callback === 'function') callback(data);
        try {
          const eventType = String(data?.type || '').toUpperCase();
          if (realmType === 'Reminder' && (
            eventType === 'REMINDER'
            || eventType === 'REMINDER_INVITE'
            || eventType === 'REMINDER_ACCEPTED'
            || eventType === 'REMINDER_REJECTED'
          )) {
            return;
          }
          const payload = data?.payload || data?.task || data?.reminder || data;
          if (payload && (payload.id || payload._id) && !Array.isArray(payload.items)) {
            upsertRealmDocs(realmType, payload);
          }
        } catch (err) {
          console.warn(`[realm] ws ${realmType} write failed`, err);
        }
      };
      return original(wrapped);
    };
  };

  wrapUserQueue('subscribeToReminders', 'Reminder');
  wrapUserQueue('subscribeToTasks', 'Task');
};

export const backfillChannelMessages = async (
  channelId,
  beforeIso,
  { limit = 80, timeout = 4000 } = {},
) => {
  if (!channelId || !(await realmAvailable())) return null;
  try {
    const response = await axios.get(`${API_URL}/sync/messages`, {
      headers: authHeaders(),
      params: { channelId, before: beforeIso, limit },
      timeout,
    });
    if (response.data?.messages?.length) {
      await upsertRealmDocs('Message', response.data.messages);
    }
    return response.data;
  } catch (err) {
    console.warn('[realm] message backfill failed', err?.message || err);
    return null;
  }
};

/**
 * Platform-agnostic helper for clients (Realm/Isar/SQLite/etc):
 * backend sync APIs remain the same; only this storage adapter changes.
 */
export const createSyncClient = ({
  openStore,
  closeStore,
  applySnapshot,
  getCheckpoint,
  setCheckpoint,
  request,
}) => {
  if (!openStore || !applySnapshot || !getCheckpoint || !setCheckpoint || !request) {
    throw new Error('createSyncClient requires openStore/applySnapshot/getCheckpoint/setCheckpoint/request');
  }
  return {
    async bootstrap({ userId, messagesPerChannel = 80 } = {}) {
      await openStore(userId);
      const payload = await request('/sync/bootstrap', { messagesPerChannel });
      await applySnapshot(payload);
      await setCheckpoint(payload.serverCursor || payload.syncedAt || null);
      return payload;
    },
    async delta({ messagesPerChannel = 50 } = {}) {
      const since = await getCheckpoint();
      if (!since) return null;
      const payload = await request('/sync/delta', { since, messagesPerChannel });
      await applySnapshot(payload);
      await setCheckpoint(payload.serverCursor || payload.syncedAt || since);
      return payload;
    },
    async close({ wipe = false } = {}) {
      if (closeStore) {
        await closeStore({ wipe });
      }
    },
  };
};
